import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { default as publicAxios } from "axios";
import { axios, APIBASE_REVIVERUSERS } from "./ApiConfig";
import { WhiteLabelProperties, WhiteLabelPublicContext } from "@/types";
import { arrayBufferToBase64 } from "@/utils/arrayBufferToBase64";

const getWhiteLabelProperties = async (whiteLabelId: number) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/white-label/${whiteLabelId}/properties`);

export const useGetWhiteLabelProperties = (
  whiteLabelId: number,
  options?: Partial<UseQueryOptions<WhiteLabelProperties>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery({
    queryKey: ["getWhiteLabelProperties", whiteLabelId],
    queryFn: async () => {
      const result = await getWhiteLabelProperties(whiteLabelId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const putUpdateWhiteLabelProperties = async (whiteLabelId: number, properties: { [key: string]: string }) => {
  await axios.put(`${APIBASE_REVIVERUSERS}api/white-label/${whiteLabelId}/properties`, properties);
};

const getWhiteLabelContext = async () =>
  publicAxios.get(`${APIBASE_REVIVERUSERS}api/white-label/context`, {
    validateStatus: () => true,
  });

export const useWhiteLabelContext = (options?: Partial<UseQueryOptions<WhiteLabelPublicContext | null>>) => {
  const { data: whiteLabel, ...query } = useQuery<WhiteLabelPublicContext | null>({
    queryKey: ["whiteLabelContext"],
    queryFn: async () => {
      const result = await getWhiteLabelContext();
      // The backend answers 204 No Content on a non-white-label host (market.reviver.no,
      // dev.reviver.no, ...). The payload check additionally rejects a 200 carrying
      // index.html, which is what an unproxied request gets from the SPA fallback.
      if (result.status !== 200 || typeof result.data !== "object" || result.data === null) return null;
      return result.data as WhiteLabelPublicContext;
    },
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: Infinity,
    ...options,
  });

  return {
    ...query,
    whiteLabel,
    whiteLabelId: whiteLabel?.id,
    branding: whiteLabel?.branding,
    portal: whiteLabel?.portal,
  };
};

/**
 * Whether the app is being served from a white-label portal. The backend resolves the
 * tenant from the request Host header, so this is a property of the URL, not the company.
 *
 * `isSettled` is returned separately because callers need different semantics: render
 * paths fail closed (`!isSettled || isWhiteLabel`) so a restricted option can never be
 * ticked before the tenant is known, while effects must wait for a settled value rather
 * than act on a provisional one.
 */
export const useIsWhiteLabel = () => {
  const { whiteLabel, isPending, isError } = useWhiteLabelContext();

  return { isWhiteLabel: whiteLabel?.id != null, isSettled: !isPending && !isError };
};

const getBrandLogo = async (whiteLabelId: number) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/white-label/${whiteLabelId}/logo`, {
    responseType: "arraybuffer",
  });

export const useGetBrandLogo = (whiteLabelId: number, options?: Partial<UseQueryOptions<string | null>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    retry: 0,
    ...options,
  };

  return useQuery<string | null>({
    queryKey: ["getBrandLogo", whiteLabelId],
    queryFn: async () => {
      try {
        const response = await getBrandLogo(whiteLabelId);
        if (response.data.byteLength === 0) return null;
        const base64 = arrayBufferToBase64(response.data);
        const contentType = response.headers["content-type"] || "image/jpeg";
        return `data:${contentType};base64,${base64}`;
      } catch (err: any) {
        if (err?.response?.status === 404) return null;
        throw err;
      }
    },
    ...queryOptions,
  });
};

export const postBrandLogo = async (whiteLabelId: number, file: FormData) =>
  axios.post(`${APIBASE_REVIVERUSERS}api/white-label/${whiteLabelId}/logo`, file);

export const deleteBrandLogo = async (whiteLabelId: number) =>
  axios.delete(`${APIBASE_REVIVERUSERS}api/white-label/${whiteLabelId}/logo`);
